/**
 * Minimal UI Kit — Component overrides
 * Source: https://github.com/minimal-ui-kit/material-kit-react (free kit, v6.x)
 *
 * Only touches defaults — no hard-coded colours; all palette tokens resolve
 * through the theme so the CCS brand palette wires in automatically.
 */

import type { Theme, Components } from "@mui/material/styles";

export function minimalComponents(theme: Theme): Components<Theme> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        "*, *::before, *::after": {
          boxSizing: "border-box",
        },
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        a: {
          color: theme.palette.primary.main,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        },
        sizeLarge: {
          minHeight: 48,
          padding: "8px 22px",
        },
        sizeMedium: {
          padding: "6px 16px",
        },
        sizeSmall: {
          padding: "4px 10px",
        },
        outlined: {
          borderColor: theme.palette.divider,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: theme.shadows[2],
          zIndex: 0,
          position: "relative",
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "24px 24px 0",
        },
        title: {
          fontSize: "1.125rem",
          fontWeight: 700,
        },
        subheader: {
          fontSize: "0.875rem",
          color: theme.palette.text.secondary,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px",
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        outlined: {
          borderColor: theme.palette.divider,
          borderRadius: 12,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.default,
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-of-type td": {
            borderBottom: 0,
          },
          "&.MuiTableRow-hover:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.text.primary,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: theme.palette.primary.main,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: "0.875rem",
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 100,
          backgroundColor: theme.palette.action.selected,
        },
        bar: {
          borderRadius: 100,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: "none",
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingTop: 6,
          paddingBottom: 6,
          "&.Mui-selected": {
            backgroundColor: `${theme.palette.primary.main}14`,
            color: theme.palette.primary.main,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: `${theme.palette.primary.main}22`,
            },
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.palette.text.secondary,
          minWidth: "auto",
          marginRight: 12,
        },
      },
    },

    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.secondary,
          fontSize: "0.6875rem",
          fontWeight: 700,
          lineHeight: "24px",
          letterSpacing: "1.1px",
          textTransform: "uppercase",
          backgroundColor: "transparent",
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },

    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.grey[800],
          borderRadius: 6,
          fontSize: "0.75rem",
        },
        arrow: {
          color: theme.palette.grey[800],
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: theme.shadows[3],
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: "2px 6px",
          padding: "6px 10px",
        },
      },
    },
  };
}
